// The mock's shared pieces as components: chips, avatars, the wordmark, dealer
// marks, the quantity box, the right-hand sheet, toasts, empty states and the page head.
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { roleLabel, type AdminTier, type DealerBrand, type QuoteStatus } from "@phoneware/lens-domain";
import { Icon, type IconName } from "./icons.js";
import { QUOTE_STATUS, initials, type ChipKind } from "./format.js";
import { Link } from "./router.js";

/** Sets the tab title: "Q-1001 · Omni · Phoneware". */
export function useTitle(title: string | null): void {
  useEffect(() => {
    document.title = title ? `${title} \u00b7 Omni \u00b7 Phoneware` : "Omni \u00b7 Phoneware";
  }, [title]);
}

export function Chip({ kind = "", icon, children, testId, status }: { kind?: ChipKind; icon?: IconName | null; children: React.ReactNode; testId?: string; status?: string }): React.ReactElement {
  return (
    <span className={kind ? `chip ${kind}` : "chip"} data-testid={testId} data-status={status}>
      {icon ? <Icon name={icon} size={10} /> : null}
      {children}
    </span>
  );
}

export function QuoteStatusChip({ status, testId }: { status: QuoteStatus; testId?: string }): React.ReactElement {
  const look = QUOTE_STATUS[status];
  return (
    <Chip kind={look.kind} icon={look.icon} testId={testId} status={status}>
      {look.label}
    </Chip>
  );
}

/** Each Phoneware tier's mark and what it can do, as the Phoneware team and the tier pickers say it (mock M1). */
export const TIER_ICON: Record<AdminTier, IconName> = { admin_super: "badge-check", admin_billing: "receipt", admin_sales: "file-text", admin_support: "ticket" };
export const TIER_BLURB: Record<AdminTier, string> = {
  admin_super: "Everything Phoneware can do, plus the Phoneware team.",
  admin_billing: "Approvals, billing, order fixes, catalog links and syncs. No quoting.",
  admin_sales: "Quotes and new customers for Phoneware\u2019s own customers, and MAC orders. Other dealers are view only.",
  admin_support: "MAC orders from support tickets for any customer. Everything else is view only.",
};

/** A tier with its mark: "Billing ops". */
export function Tier({ tier }: { tier: AdminTier }): React.ReactElement {
  return (
    <span className="tier" data-testid={`tier-${tier}`}>
      <Icon name={TIER_ICON[tier]} size={14} />
      {roleLabel(tier)}
    </span>
  );
}

/** A screen a Phoneware tier reads without its buttons: "View only", then why. */
export function ViewOnly({ children, testId }: { children: React.ReactNode; testId?: string }): React.ReactElement {
  return (
    <div className="callout viewonly" data-testid={testId}>
      <Icon name="eye" size={15} />
      <span>
        <b>View only</b>
        {children}
      </span>
    </div>
  );
}

/** Where a decision button would be, for a tier that only watches: who decides instead. */
export function DecidedBy({ children, testId }: { children: React.ReactNode; testId?: string }): React.ReactElement {
  return (
    <p className="card-p ro" data-testid={testId}>
      <Icon name="lock" size={14} />
      <span>{children}</span>
    </p>
  );
}

export function Avatar({ name }: { name: string }): React.ReactElement {
  return (
    <span className="av" aria-hidden="true">
      {initials(name)}
    </span>
  );
}

/** Rick's Omni artwork: the bare wordmark, or on sign-in, the wordmark with Phoneware's credit set beside it. */
export function Wordmark({ className, variant = "wordmark" }: { className?: string; variant?: "wordmark" | "lockup" }): React.ReactElement {
  if (variant === "lockup") {
    // One image to assistive tech: the credit is drawn as text, and its words are announced once, through the label.
    return (
      <span className={`wm-lockup${className ? ` ${className}` : ""}`} role="img" aria-label="Omni, powered by Phoneware" data-pw-control-visual="essential-brand-mark">
        <span className="wm" />
        <span className="wm-rule" />
        <span className="wm-credit" aria-hidden="true">
          <span>Powered by</span>
          <b>Phoneware</b>
        </span>
      </span>
    );
  }
  return <span className={`wm${className ? ` ${className}` : ""}`} role="img" aria-label="Omni" data-pw-control-visual="essential-brand-mark" />;
}

/** A dealer's own mark: its logo when it has one, otherwise its initials on its color. */
export function DealerMark({ name, brand, size, onColor }: { name: string; brand: DealerBrand; size: number; onColor?: boolean }): React.ReactElement {
  if (brand.logoDataUrl) {
    return <img className="dmark" src={brand.logoDataUrl} alt="" width={size} height={size} style={{ width: size, height: size }} />;
  }
  return (
    <span
      className="dmark text"
      aria-hidden="true"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.38), background: onColor ? "rgba(255, 255, 255, .16)" : brand.color }}
    >
      {initials(name)}
    </span>
  );
}

export function Head({ title, sub, actions }: { title: React.ReactNode; sub?: React.ReactNode; actions?: React.ReactNode }): React.ReactElement {
  return (
    <div className="head">
      <div>
        <h1>{title}</h1>
        {sub ? <p className="sub">{sub}</p> : null}
      </div>
      {actions ? <div className="actions">{actions}</div> : null}
    </div>
  );
}

export function Crumb({ to, children }: { to: string; children: React.ReactNode }): React.ReactElement {
  return (
    <Link className="crumb" to={to}>
      <Icon name="arrow-left" size={14} />
      {children}
    </Link>
  );
}

export function Callout({ kind, icon, children }: { kind?: "ok" | "warn" | "stop"; icon: IconName; children: React.ReactNode }): React.ReactElement {
  return (
    <div className={kind ? `callout ${kind}` : "callout"} role={kind === "stop" ? "alert" : undefined}>
      <Icon name={icon} size={15} />
      <span>{children}</span>
    </div>
  );
}

/** A detail page for a record outside the caller's dealer, or one that does not exist. */
export function NotYours({ kind, back }: { kind: string; back: string }): React.ReactElement {
  return (
    <>
      <Crumb to={back}>Back</Crumb>
      <div className="card notyours">
        <span className="ny-ic">
          <Icon name="lock" size={20} />
        </span>
        <div>
          <h1>Not one of yours</h1>
          <p>
            This {kind} does not exist or belongs to another dealer. Omni shows each dealer its own customers, quotes and orders, and
            nothing else.
          </p>
          <Link className="btn" to={back}>
            Back to your {kind}s
          </Link>
        </div>
      </div>
    </>
  );
}

/** A whole number of at least one. Typing keeps the text as typed; leaving the box shows what was kept. */
export function QtyBox({ qty, onQty, label, testId }: { qty: number; onQty: (n: number) => void; label: string; testId: string }): React.ReactElement {
  const [text, setText] = useState(String(qty));
  const editing = useRef(false);
  useEffect(() => {
    if (!editing.current) setText(String(qty));
  }, [qty]);
  return (
    <label>
      <span className="sr">{label}</span>
      <input
        className="input qty"
        type="number"
        min={1}
        step={1}
        data-testid={testId}
        value={text}
        onFocus={() => {
          editing.current = true;
        }}
        onChange={(e) => {
          setText(e.target.value);
          const n = Number(e.target.value);
          if (Number.isInteger(n) && n >= 1) onQty(n);
        }}
        onBlur={() => {
          editing.current = false;
          setText(String(qty));
        }}
      />
    </label>
  );
}

/** While any Sheet is open the signed-in portal behind it is inert; the last Sheet to close puts back how it was. */
const portalHold = { sheets: 0, app: null as HTMLElement | null, wasInert: false, listeners: new Set<() => void>() };

/**
 * Whether a Sheet holds the portal. The request batch dock lives on the body, outside .app, so it reads this to hide
 * and go inert with the portal: Tab and the reading order never leave an open Sheet for it.
 */
export function useSheetHeld(): boolean {
  return useSyncExternalStore(watchHold, isHeld, isHeldOnServer);
}

function watchHold(listener: () => void): () => void {
  portalHold.listeners.add(listener);
  return () => {
    portalHold.listeners.delete(listener);
  };
}

function isHeld(): boolean {
  return portalHold.sheets > 0;
}

function isHeldOnServer(): boolean {
  return false;
}

/** Holds the portal inert for one Sheet. Releasing says whether it was the last open Sheet's hold. */
function holdPortal(): () => boolean {
  if (portalHold.sheets++ === 0) {
    const app = document.querySelector<HTMLElement>(".app");
    portalHold.app = app;
    portalHold.wasInert = app?.inert ?? false;
    if (app) app.inert = true;
    for (const listener of portalHold.listeners) listener();
  }
  let held = true;
  return () => {
    if (!held) return false;
    held = false;
    if (--portalHold.sheets > 0) return false;
    if (portalHold.app) portalHold.app.inert = portalHold.wasInert;
    portalHold.app = null;
    for (const listener of portalHold.listeners) listener();
    return true;
  };
}

function hasInertAncestor(element: Element): boolean {
  for (let node: Element | null = element; node; node = node.parentElement) {
    if (node.hasAttribute("inert") || (node instanceof HTMLElement && node.inert)) return true;
  }
  return false;
}

const NATIVE_TABBABLE =
  "a[href], area[href], button, input:not([type='hidden']), select, textarea, iframe, summary, audio[controls], video[controls], [contenteditable]:not([contenteditable='false'])";

/** Drawn inside the dialog: not hidden or display: none up to it, and not visibility: hidden. */
function rendered(element: HTMLElement, sheet: HTMLElement): boolean {
  const visibility = window.getComputedStyle(element).visibility;
  if (visibility === "hidden" || visibility === "collapse") return false;
  for (let node: HTMLElement | null = element; node && node !== sheet; node = node.parentElement) {
    if (node.hidden || window.getComputedStyle(node).display === "none") return false;
  }
  return true;
}

/** The Sheet's enabled, visible, non-inert controls that Tab reaches, in document order. */
function tabStops(sheet: HTMLElement): HTMLElement[] {
  return Array.from(sheet.querySelectorAll<HTMLElement>(`${NATIVE_TABBABLE}, [tabindex]`)).filter((element) => {
    const raw = element.getAttribute("tabindex");
    const order = raw === null ? Number.NaN : Number.parseInt(raw, 10);
    if (Number.isNaN(order) ? !element.matches(NATIVE_TABBABLE) : order < 0) return false;
    return !element.matches(":disabled") && rendered(element, sheet) && !hasInertAncestor(element);
  });
}

/** Keeps Tab and Shift+Tab in the topmost Sheet: from the dialog itself, or off either end, focus goes to the other end. */
function trapTab(sheet: HTMLElement, event: KeyboardEvent): void {
  const open = document.querySelectorAll(".sheet[role='dialog']");
  if (open[open.length - 1] !== sheet) return;
  const stops = tabStops(sheet);
  const active = document.activeElement;
  // A control that removes itself on Tab drops focus to the body; the browser still continues from
  // where it was inside the Sheet, and the inert portal behind it can't take focus.
  if (!active || active === document.body) return;
  let wrap = true;
  if (active instanceof HTMLElement && active !== sheet && sheet.contains(active)) {
    // Leaving an end: no stop before the focus for Shift+Tab, or none after it for Tab.
    const after = Node.DOCUMENT_POSITION_FOLLOWING;
    wrap = event.shiftKey ? !stops.some((stop) => stop.compareDocumentPosition(active) & after) : !stops.some((stop) => active.compareDocumentPosition(stop) & after);
  }
  if (!wrap) return;
  event.preventDefault();
  (stops[event.shiftKey ? stops.length - 1 : 0] ?? sheet).focus({ preventScroll: true });
}

export function Sheet({
  title,
  label,
  meta,
  onClose,
  wide,
  foot,
  children,
}: {
  title: string;
  /** What a screen reader announces for the dialog, when it says more than the title. */
  label?: string;
  /** A short reference beside the title, such as the order number. */
  meta?: React.ReactNode;
  onClose: () => void;
  wide?: boolean;
  foot?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  const [shown, setShown] = useState(false);
  const dialog = useRef<HTMLDivElement>(null);
  // Read while rendering, before the dialog or a child's autoFocus takes focus.
  const [opener] = useState<HTMLElement | null>(() => {
    const active = document.activeElement;
    return active instanceof HTMLElement && active !== document.body ? active : null;
  });

  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  useLayoutEffect(() => {
    const release = holdPortal();
    const root = dialog.current;
    // A child's autoFocus, such as the product search, is already inside; otherwise the dialog takes focus.
    if (root && !root.contains(document.activeElement)) root.focus({ preventScroll: true });
    return () => {
      if (!release()) return;
      // Once React has removed the dialog: back to the opener, unless it is gone, inert again, or a new Sheet has focus.
      queueMicrotask(() => {
        const active = document.activeElement;
        const sheetHasFocus = active !== null && Array.from(document.querySelectorAll(".sheet[role='dialog']")).some((sheet) => sheet.contains(active));
        if (!opener?.isConnected || hasInertAncestor(opener) || sheetHasFocus) return;
        opener.focus({ preventScroll: true });
      });
    };
  }, [opener]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "Tab" && dialog.current) {
        trapTab(dialog.current, e);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <>
      <div className={shown ? "scrim show" : "scrim"} onClick={onClose} />
      <div ref={dialog} className={`sheet${wide ? " wide" : ""}${shown ? " show" : ""}`} role="dialog" aria-modal="true" aria-label={label ?? title} tabIndex={-1}>
        <div className="sheet-h">
          <h2>{title}</h2>
          {meta ? <span className="crumbish">{meta}</span> : null}
          <button className="iconbtn" type="button" aria-label="Close" onClick={onClose}>
            <Icon name="x" />
          </button>
        </div>
        <div className="sheet-b">{children}</div>
        {foot ? <div className="sheet-f">{foot}</div> : null}
      </div>
    </>,
    document.body,
  );
}

type Toast = (text: string) => void;
const ToastContext = createContext<Toast>(() => undefined);

export function useToast(): Toast {
  return useContext(ToastContext);
}

export function ToastHost({ children }: { children: React.ReactNode }): React.ReactElement {
  const [text, setText] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const show = useCallback<Toast>((next) => {
    setText(next);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setText(null), 3200);
  }, []);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className={text ? "toast show" : "toast"} role="status" aria-live="polite">
        {text ? (
          <>
            <Icon name="check" size={15} />
            <span>{text}</span>
          </>
        ) : null}
      </div>
    </ToastContext.Provider>
  );
}
